import { maskVendor } from "@/shared/lib/vendorMask";
import { Button } from "@/shared/ui/button";
import { Textarea } from "@/shared/ui/textarea";
import { VoiceoverCapHint } from "@/shared/ui/VoiceoverCapHint";

import {
  CHUNK_EDIT_COST_HINT,
  changedShots,
  chunkEditDirty,
  type ChunkEditDraft,
} from "../../model/chunkEdit";
import { editImpact } from "../../model/chunkEditImpact";
import type { ShotEditForm } from "../../model/shotEditForm";

/**
 * 失败段「改文案再重渲」面板(VID-D,2026-08-18 老板走查:
 * 「各分段生成中经常有失败的段,可以重新渲染,但没有地方去改文案」)。
 *
 * <p>一段含多镜,所以这里是**逐镜**三栏(画面/动作/口播)。两个出口刻意分开:
 * 「只保存」不花钱、「保存并重渲」按段计费 —— 用户最容易误以为改个错别字也要再扣一次。
 */
export function VideoChunkTextEditor({
  draft,
  onChange,
  onCancel,
  onSave,
  saving,
  disabled,
  shotDurations,
}: {
  draft: ChunkEditDraft;
  onChange: (shotSeq: number, patch: Partial<ShotEditForm>) => void;
  onCancel: () => void;
  /** rerender=true → 存完接着重渲本段(花积分);false → 只存文案(不花钱)。 */
  onSave: (rerender: boolean) => void;
  saving?: boolean;
  /** 任务正在跑:存可以,重渲不行(重渲一个正在渲的东西会真开一张计费 ticket)。 */
  disabled?: boolean;
  /** 逐镜时长(秒,下标 = 镜序 - 1);有就在口播框下提示念得下多少字、哪些会被截掉。 */
  shotDurations?: number[];
}) {
  const dirty = chunkEditDirty(draft);
  const impact = editImpact(draft);
  const n = changedShots(draft).length;

  return (
    <div
      data-testid={`chunk-editor-${draft.seq}`}
      className="space-y-3 rounded-lg border bg-muted/20 p-3"
    >
      <p className="text-xs font-medium">改第 {draft.seq} 段的文案</p>
      {/* 为什么失败 —— 就摆在要改的框子上面,照着它改 */}
      {draft.failure && (
        <div
          data-testid="chunk-editor-failure"
          className="rounded bg-destructive/10 px-2 py-1.5 text-xs text-destructive"
        >
          <p className="leading-snug">{maskVendor(draft.failure.reason)}</p>
          {draft.failure.code && (
            <p className="mt-0.5 font-mono text-2xs opacity-60">{draft.failure.code}</p>
          )}
        </div>
      )}

      {/**
       * ★分两区,不再三栏平级(2026-08-19 老板走查「改文案那里现在能改的是传给 seedance 的
       *   原文么」)。答案分栏不同,而此前界面没说:
       *     画面/运动 → 进 ai `compile_chunk_prompt`,**就是发给 seedance 的那段原文**
       *     口播      → 只进 TTS 与烧字幕,那段 prompt 里反而写着「全程人物闭口不出声」
       *   三栏长得一样,于是只改了个错别字口播的人照样去点满宽紫色的「保存并重渲本段」,
       *   白花一次段渲染的钱。把「花不花钱」直接做进版面。
       */}
      <div
        data-testid="chunk-edit-visual-group"
        className="space-y-1.5 rounded-md border bg-background p-2"
      >
        <p className="text-2xs font-medium">
          影响画面
          <span className="ml-1 font-normal text-muted-foreground">
            —— 这两栏就是发给视频模型的原文,改了要<b>重渲</b>才生效(按段计费)
          </span>
        </p>
        {draft.shots.map(({ shotSeq, form }) => (
          <div key={shotSeq} className="space-y-1.5">
            <p className="font-mono text-2xs text-muted-foreground">镜 {shotSeq}</p>
            <Field
              label="画面(这一镜拍的是什么)"
              value={form.panelPrompt}
              rows={2}
              placeholder="被审核拒时,把敏感/绝对化的说法换掉"
              onChange={(v) => onChange(shotSeq, { panelPrompt: v })}
            />
            <Field
              label="运动 / 动作"
              value={form.panelMotion}
              rows={1}
              placeholder="例:镜头缓慢推近"
              onChange={(v) => onChange(shotSeq, { panelMotion: v })}
            />
          </div>
        ))}
      </div>

      <div
        data-testid="chunk-edit-voice-group"
        className="space-y-1.5 rounded-md border bg-background p-2"
      >
        <p className="text-2xs font-medium">
          不影响画面
          <span className="ml-1 font-normal text-muted-foreground">
            —— 只改配音与字幕,下次「合成成片」就生效,<b>不用重渲、不花钱</b>
          </span>
        </p>
        {draft.shots.map(({ shotSeq, form }) => (
          <div key={shotSeq} className="space-y-1">
            <p className="font-mono text-2xs text-muted-foreground">镜 {shotSeq}</p>
            <Field
              label={`镜 ${shotSeq} 口播`}
              value={form.voiceover}
              rows={2}
              onChange={(v) => onChange(shotSeq, { voiceover: v })}
            />
            <VoiceoverCapHint
              text={form.voiceover}
              durationSec={shotDurations?.[shotSeq - 1]}
              subject="本镜"
            />
          </div>
        ))}
      </div>

      {/* 按这次实际改了什么说一句话 —— 比两条恒定的费用说明有用得多 */}
      <p
        data-testid="chunk-edit-impact-hint"
        className="text-2xs leading-snug text-muted-foreground"
      >
        {impact === "voice"
          ? "这次只动了口播 —— 直接「只保存」就行,不用重渲、不花钱;下次合成成片时生效。"
          : impact === "visual"
            ? "这次动了画面/运动 —— 光保存不会改变已渲好的画面,要「保存并重渲本段」才生效(按段计费)。"
            : `${CHUNK_EDIT_COST_HINT.saveOnly} ${CHUNK_EDIT_COST_HINT.saveAndRender}`}
      </p>

      {/**
       * ★主按钮跟着 impact 走,不写死。文案刻意不动(既有 chunkedit 锁按文案定位),
       *   变的是**哪一颗是实心**:只改口播时把人往「只保存」引,别让他为两句台词重渲一整段。
       */}
      <div className="flex gap-2">
        <Button
          size="sm"
          variant={impact === "voice" ? "outline" : "default"}
          className={impact === "voice" ? "text-xs" : "flex-1 text-xs"}
          disabled={!dirty || saving || disabled}
          onClick={() => onSave(true)}
          title={disabled ? "任务正在生成,等这一轮跑完再重渲" : undefined}
        >
          {saving ? "提交中…" : "保存并重渲本段"}
        </Button>
        <Button
          size="sm"
          variant={impact === "voice" ? "default" : "outline"}
          className={impact === "voice" ? "flex-1 text-xs" : "text-xs"}
          disabled={!dirty || saving}
          onClick={() => onSave(false)}
        >
          只保存{n > 0 ? `(${n} 镜)` : ""}
        </Button>
        <Button size="sm" variant="ghost" className="text-xs" disabled={saving} onClick={onCancel}>
          取消
        </Button>
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  rows,
  placeholder,
  onChange,
}: {
  label: string;
  value: string;
  rows: number;
  placeholder?: string;
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <p className="mb-0.5 text-2xs text-muted-foreground">{label}</p>
      <Textarea
        rows={rows}
        value={value}
        aria-label={label}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="text-xs"
      />
    </div>
  );
}
